//
// Copyright 2022 DXOS.org
//

import { invariant } from '@dxos/invariant';
import { PublicKey } from '@dxos/keys';
import { toPublicKey } from '@dxos/protocols/buf';
import { type AdmittedFeed, type Credential } from '@dxos/protocols/buf/dxos/halo/credentials_pb';
import { type AsyncCallback, Callback, ComplexMap } from '@dxos/util';

import { getCredentialAssertion, subjectIdOf } from '../credentials/index.ts';

export interface FeedInfo {
  key: PublicKey;
  /**
   * Parent feed from the feed tree.
   * This is the feed where the AdmittedFeed assertion is written.
   * The genesis feed will have itself as a parent.
   */
  parent: PublicKey;
  credential: Credential;
  assertion: AdmittedFeed;
}

/**
 * Tracks the feed tree for a space.
 * Provides a list of admitted feeds.
 */
export class FeedStateMachine {
  private _feeds = new ComplexMap<PublicKey, FeedInfo>(PublicKey.hash);

  readonly onFeedAdmitted = new Callback<AsyncCallback<FeedInfo>>();

  constructor(private readonly _spaceKey: PublicKey) {}

  get feeds(): ReadonlyMap<PublicKey, FeedInfo> {
    return this._feeds;
  }

  /**
   * Processes the AdmittedFeed credential.
   * Assumes the credential is already pre-verified
   * and the issuer has been authorized to issue credentials of this type.
   * @param fromFeed Key of the feed where this credential is recorded.
   */
  async process(credential: Credential, fromFeed: PublicKey): Promise<void> {
    const assertion = getCredentialAssertion(credential);
    invariant(assertion.$typeName === 'dxos.halo.credentials.AdmittedFeed');
    invariant(toPublicKey(assertion.spaceKey)?.equals(this._spaceKey));

    const feedKey = subjectIdOf(credential);
    const info: FeedInfo = {
      key: feedKey,
      credential,
      assertion,
      parent: fromFeed,
    };

    this._feeds.set(feedKey, info);
    await this.onFeedAdmitted.callIfSet(info);
  }
}
